/**
 * Shrinks a screenshot to a JPEG data URL small enough to upload.
 *
 * Vercel caps a request at 4.5 MB, and a phone screenshot can be 3–5 MB as a
 * PNG, so three of them base64-encoded would never arrive. Each one is drawn
 * onto a canvas no larger than MAX_SIDE on its long edge and re-encoded as
 * JPEG, stepping the quality (then the size) down until it fits TARGET_BYTES.
 * Text in a screenshot stays readable at these settings.
 */
const MAX_SIDE = 2000;
export const TARGET_BYTES = 900 * 1024;
const QUALITIES = [0.85, 0.75, 0.65];

/** Bytes a base64 data URL decodes to. */
export function dataUrlBytes(dataUrl) {
  const base64 = String(dataUrl).split(',')[1] || '';
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('That image couldn’t be read. Try a screenshot (PNG or JPEG).')); };
    img.src = url;
  });
}

export async function compressScreenshot(file) {
  if (!file || !/^image\//.test(file.type || '')) throw new Error('Choose an image (a screenshot works best).');
  const img = await loadImage(file);
  let scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth || 1, img.naturalHeight || 1));
  for (let round = 0; round < 4; round += 1) {
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    for (const q of QUALITIES) {
      const dataUrl = canvas.toDataURL('image/jpeg', q);
      if (dataUrlBytes(dataUrl) <= TARGET_BYTES) return dataUrl;
    }
    scale *= 0.75;
  }
  throw new Error('That image is too large. Try a smaller screenshot.');
}
